import { X } from 'lucide-react'
import type { RequirementTheme } from '@ispot/shared'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import { REQUIREMENT_TYPE_LABELS, REQUIREMENT_PRIORITY_LABELS, REQUIREMENT_STATUS_LABELS } from '../constants'

interface Props {
  themes: RequirementTheme[]
  /** When true, render an empty placeholder instead of nothing (keeps layout height stable). */
  emptyPlaceholder?: boolean
}

/**
 * Horizontal strip showing every active facet as a removable chip + an
 * optional "Clear all" link. Backed entirely by the store so any view can
 * drop this in and stay in sync.
 *
 * Renders nothing when no filters are active (unless `emptyPlaceholder` is set).
 */
export function ActiveFilterChips({ themes, emptyPlaceholder }: Props) {
  const facetTypes = useRequirementsViewStore(s => s.facetTypes)
  const facetPriorities = useRequirementsViewStore(s => s.facetPriorities)
  const facetStatuses = useRequirementsViewStore(s => s.facetStatuses)
  const facetThemeIds = useRequirementsViewStore(s => s.facetThemeIds)
  const searchQuery = useRequirementsViewStore(s => s.searchQuery)
  const toggleFacet = useRequirementsViewStore(s => s.toggleFacet)
  const clearFacets = useRequirementsViewStore(s => s.clearFacets)
  const setSearchQuery = useRequirementsViewStore(s => s.setSearchQuery)

  const themeById = new Map(themes.map(t => [t.id, t]))
  const total =
    facetTypes.size + facetPriorities.size + facetStatuses.size
    + facetThemeIds.size + (searchQuery.trim() ? 1 : 0)

  if (total === 0) {
    if (!emptyPlaceholder) return null
    return (
      <div
        className="flex items-center"
        style={{
          minHeight: 28,
          fontSize: 11.5,
          color: 'var(--color-fg-muted)',
        }}
      >
        No active filters
      </div>
    )
  }

  return (
    <div className="flex items-center flex-wrap" style={{ gap: 6, minHeight: 28 }}>
      {searchQuery.trim() && (
        <Chip label="Search" value={`"${searchQuery}"`} onRemove={() => setSearchQuery('')} />
      )}
      {[...facetTypes].map(v => (
        <Chip
          key={`t-${v}`}
          label="Type"
          value={REQUIREMENT_TYPE_LABELS[v as keyof typeof REQUIREMENT_TYPE_LABELS] ?? v}
          onRemove={() => toggleFacet('types', v)}
        />
      ))}
      {[...facetPriorities].map(v => (
        <Chip
          key={`p-${v}`}
          label="Priority"
          value={REQUIREMENT_PRIORITY_LABELS[v as keyof typeof REQUIREMENT_PRIORITY_LABELS] ?? v}
          onRemove={() => toggleFacet('priorities', v)}
        />
      ))}
      {[...facetStatuses].map(v => (
        <Chip
          key={`s-${v}`}
          label="Status"
          value={REQUIREMENT_STATUS_LABELS[v as keyof typeof REQUIREMENT_STATUS_LABELS] ?? v}
          onRemove={() => toggleFacet('statuses', v)}
        />
      ))}
      {[...facetThemeIds].map(v => (
        <Chip
          key={`th-${v}`}
          label="Theme"
          value={themeById.get(v)?.name ?? v}
          onRemove={() => toggleFacet('themeIds', v)}
        />
      ))}
      {total > 1 && (
        <button
          onClick={() => { clearFacets(); setSearchQuery('') }}
          className="text-[11.5px] font-medium px-2 py-0.5 rounded cursor-pointer border-0 bg-transparent"
          style={{ color: 'var(--color-fg-muted)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-accent)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fg-muted)')}
        >
          Clear all
        </button>
      )}
    </div>
  )
}

function Chip({ label, value, onRemove }: { label: string; value: string; onRemove: () => void }) {
  return (
    <span
      className="inline-flex items-center"
      style={{
        gap: 4,
        height: 22,
        padding: '0 4px 0 8px',
        background: 'var(--color-bg-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 999,
        fontSize: 11,
        color: 'var(--color-fg-secondary)',
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: 'var(--color-fg-muted)' }}>{label}:</span>
      <span style={{ color: 'var(--color-fg)', fontWeight: 500 }}>{value}</span>
      <button
        onClick={onRemove}
        aria-label={`Remove ${label} ${value} filter`}
        className="rounded cursor-pointer border-0 bg-transparent inline-flex items-center justify-center"
        style={{ width: 14, height: 14, color: 'var(--color-fg-faint)', padding: 0 }}
        onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--color-fg)')}
        onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--color-fg-faint)')}
      >
        <X size={9} />
      </button>
    </span>
  )
}
